/**
 * O roteador — 40 linhas, nenhuma dependência, e um motivo para cada escolha.
 *
 * O `App.jsx` dizia "ainda não há roteador; a navegação nasce junto da segunda
 * tela, quando houver o que comparar entre elas". Chegou a segunda tela.
 *
 * **Por que hash (`#/substratos`) e não History API (`/substratos`).** Três
 * motivos, e nenhum deles é preferência:
 *
 * 1. Com History API, recarregar `/substratos` manda `GET /substratos` para o
 *    servidor, que precisa devolver o `index.html` em vez de 404. Isso é
 *    configuração de nginx (`try_files`), e passa a ser peça de que a
 *    navegação depende. O deploy deste app ainda não existe; apostar a
 *    navegação numa configuração que ninguém escreveu é criar um defeito que
 *    só aparece em produção.
 * 2. A promessa é funcionar sem sinal. Um PWA aberto do cache resolve o hash
 *    sem falar com servidor nenhum — o fragmento nunca sai do aparelho. Com
 *    rota de caminho, a primeira abertura offline numa tela que não seja a
 *    raiz depende do service worker ter acertado a estratégia de fallback.
 * 3. Link vira `<a href="#/substratos">` de verdade. Ganha de graça o clique
 *    do meio, o "abrir em nova aba", o foco por teclado e o anúncio correto
 *    do leitor de tela. Roteador com `<Link>` próprio costuma perder metade
 *    disso, e quem paga é quem navega por teclado.
 *
 * O preço do hash é a URL mais feia e nenhuma vantagem de SEO no fragmento.
 * Para um app que o produtor instala no celular, nenhum dos dois pesa; se um
 * dia a parte pública de marketing precisar de URL indexável, ela é outra
 * entrega e pode ser renderizada estaticamente.
 *
 * O estado da rota mora no `window.location`, não num `useState`. Assim o
 * botão "voltar" do Android funciona sem código nenhum — e esse botão é o
 * primeiro que o produtor aperta.
 */
import { useEffect, useState } from 'react';

/** A tela que abre quando não há hash nenhum. */
export const TELA_INICIAL = 'simulador';

/**
 * Lê `#/substratos/vaca_leiteira` como `{ tela: 'substratos', parte: 'vaca_leiteira' }`.
 *
 * Tolera as formas que aparecem na vida real: sem hash, `#`, `#/`, com barra
 * sobrando no fim, e com acento ou espaço codificados. Rota desconhecida não
 * é tratada aqui — quem decide o que fazer com ela é o `App`, porque "tela
 * que não existe" é assunto de produto e não de parsing.
 */
export function lerRota(hash) {
  const cru = String(hash ?? '').replace(/^#/, '');
  const partes = cru.split('/').filter((p) => p !== '');
  if (partes.length === 0) return { tela: TELA_INICIAL, parte: null };
  return {
    tela: decodeURIComponent(partes[0]),
    parte: partes.length > 1 ? decodeURIComponent(partes[1]) : null,
  };
}

/** Monta o hash de uma rota. Usar isto em vez de concatenar à mão. */
export function paraRota(tela, parte = null) {
  const base = `#/${encodeURIComponent(tela)}`;
  return parte ? `${base}/${encodeURIComponent(parte)}` : base;
}

/** A rota de agora, já lida. Fora do navegador devolve a inicial. */
export function rotaAtual() {
  if (typeof window === 'undefined') return { tela: TELA_INICIAL, parte: null };
  return lerRota(window.location.hash);
}

/**
 * A rota, reagindo ao `hashchange`.
 *
 * Devolve `{ tela, parte }`. O `hashchange` cobre tudo: clique em `<a>`,
 * botão voltar, e `irPara`. Nenhuma tela precisa avisar o roteador de nada.
 */
export function useRota() {
  const [rota, setRota] = useState(rotaAtual);

  useEffect(() => {
    const atualizar = () => setRota(rotaAtual());
    window.addEventListener('hashchange', atualizar);
    // Entre o primeiro render e este efeito o hash pode ter mudado (link
    // clicado durante a hidratação). Reler custa nada e evita a tela errada.
    atualizar();
    return () => window.removeEventListener('hashchange', atualizar);
  }, []);

  return rota;
}

/**
 * Navega por código.
 *
 * Só para quando não há `<a>` possível — depois de uma ação, por exemplo. Se
 * existe um link, use o link: ele é melhor para quem navega por teclado.
 */
export function irPara(tela, parte = null) {
  if (typeof window === 'undefined') return;
  window.location.hash = paraRota(tela, parte);
}

/**
 * Rola para o topo quando a tela troca.
 *
 * Sem isto, ir da lista de substratos (rolada até o meio) para o detalhe abre
 * o detalhe no meio. O navegador guarda a posição de rolagem por documento, e
 * para ele isto é um documento só.
 *
 * Não rola quando só a `parte` muda dentro da mesma tela e a tela pediu para
 * cuidar disso sozinha — daí o parâmetro ser a chave inteira da rota.
 */
export function useRolarAoTrocar(chave) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [chave]);
}
